/**
 * DataTable Component
 *
 * Reusable data table with sorting, filtering, and pagination
 */

import { type ReactNode } from 'react';
import '@tylertech/forge/table';
import '@tylertech/forge/icon';
import { LoadingSpinner } from './LoadingSpinner';
import { EmptyState } from './EmptyState';
import './DataTable.css';

export type SortDirection = 'asc' | 'desc' | null;

export interface DataTableColumn<T> {
  /** Unique column key */
  key: string;
  /** Column header label */
  label: string;
  /** Is column sortable */
  sortable?: boolean;
  /** Column width (CSS value) */
  width?: string;
  /** Custom render function */
  render?: (row: T) => ReactNode;
  /** Accessor function to get cell value */
  accessor?: (row: T) => unknown;
}

export interface DataTableProps<T> {
  /** Array of column definitions */
  columns: DataTableColumn<T>[];
  /** Array of data rows */
  data: T[];
  /** Row key accessor function */
  getRowKey: (row: T) => string;
  /** Loading state */
  loading?: boolean;
  /** Empty state configuration */
  emptyState?: {
    icon?: string;
    title: string;
    description?: string;
    actionLabel?: string;
    onAction?: () => void;
  };
  /** Current sort column key */
  sortColumn?: string;
  /** Current sort direction */
  sortDirection?: SortDirection;
  /** Sort change handler */
  onSort?: (column: string, direction: SortDirection) => void;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Optional extra class name(s) applied to each row (e.g. grouping accents) */
  rowClassName?: (row: T) => string;
  /** Enable row checkboxes for multi-select */
  selectable?: boolean;
  /** Currently selected row keys (controlled) */
  selectedIds?: Set<string>;
  /** Called when the selected set changes */
  onSelectionChange?: (ids: Set<string>) => void;
}

/**
 * Data table component with sorting and custom rendering
 *
 * @example
 * ```tsx
 * <DataTable
 *   columns={[
 *     { key: 'name', label: 'Name', sortable: true },
 *     { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} /> }
 *   ]}
 *   data={services}
 *   getRowKey={(row) => row.id}
 *   onSort={(column, direction) => setSortState({ column, direction })}
 *   emptyState={{ title: 'No services found' }}
 * />
 * ```
 */
export function DataTable<T>({
  columns,
  data,
  getRowKey,
  loading = false,
  emptyState,
  sortColumn,
  sortDirection,
  onSort,
  onRowClick,
  rowClassName,
  selectable = false,
  selectedIds,
  onSelectionChange,
}: DataTableProps<T>): JSX.Element {
  const allVisibleKeys = data.map(getRowKey);
  const allSelected =
    allVisibleKeys.length > 0 && allVisibleKeys.every((key) => selectedIds?.has(key));
  const someSelected = !allSelected && allVisibleKeys.some((key) => selectedIds?.has(key));

  const handleSelectAll = (): void => {
    if (!onSelectionChange) return;
    if (allSelected) {
      const next = new Set(selectedIds);
      allVisibleKeys.forEach((key) => next.delete(key));
      onSelectionChange(next);
    } else {
      const next = new Set(selectedIds);
      allVisibleKeys.forEach((key) => next.add(key));
      onSelectionChange(next);
    }
  };

  const handleSelectRow = (key: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onSelectionChange(next);
  };

  const handleSort = (columnKey: string): void => {
    if (!onSort) return;

    const column = columns.find((col) => col.key === columnKey);
    if (!column?.sortable) return;

    let newDirection: SortDirection = 'asc';
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = null;
      }
    }

    onSort(columnKey, newDirection);
  };

  const getSortIcon = (columnKey: string): ReactNode => {
    if (sortColumn !== columnKey) {
      return <span className="sort-icon sort-icon-neutral">⇅</span>;
    }
    if (sortDirection === 'asc') {
      return <span className="sort-icon sort-icon-asc">↑</span>;
    }
    if (sortDirection === 'desc') {
      return <span className="sort-icon sort-icon-desc">↓</span>;
    }
    return <span className="sort-icon sort-icon-neutral">⇅</span>;
  };

  if (loading) {
    return <LoadingSpinner centered message="Loading data..." />;
  }

  if (data.length === 0 && emptyState) {
    return <EmptyState {...emptyState} />;
  }

  return (
    <div className="data-table">
      <table>
        <thead>
          <tr>
            {selectable && (
              <th className="data-table-checkbox-cell">
                <input
                  type="checkbox"
                  className="data-table-checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={handleSelectAll}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {columns.map((column) => (
              <th key={column.key} style={{ width: column.width }}>
                {column.sortable ? (
                  <button
                    className="data-table-sort-button"
                    onClick={() => handleSort(column.key)}
                    type="button"
                  >
                    {column.label}
                    {getSortIcon(column.key)}
                  </button>
                ) : (
                  column.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const key = getRowKey(row);
            const isSelected = selectedIds?.has(key) ?? false;
            return (
              <tr
                key={key}
                className={[
                  onRowClick ? 'data-table-row-clickable' : '',
                  isSelected ? 'data-table-row-selected' : '',
                  rowClassName?.(row) ?? '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onRowClick?.(row)}
              >
                {selectable && (
                  <td className="data-table-checkbox-cell" onClick={(e) => handleSelectRow(key, e)}>
                    <input
                      type="checkbox"
                      className="data-table-checkbox"
                      checked={isSelected}
                      onChange={() => undefined}
                      aria-label={`Select row`}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.render
                      ? column.render(row)
                      : column.accessor
                        ? String(column.accessor(row))
                        : ''}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
